import React, { useState, useRef, useEffect, useCallback } from 'react';
import { generarContratoGymartPDF } from './Gymartpdfgenerator';
import styles from './modal_contrato.module.css';

export const ModalContrato = ({ isOpen, onClose, userId, userName }) => {
  const [formData, setFormData] = useState({
    nombreMenor: userName || '',
    tipoDocumentoMenor: 'TI',
    numeroDocumentoMenor: '',
    tipoResponsable: 'madre',
    nombrePadre: '',
    cedulaPadre: '',
    nombreMadre: '',
    cedulaMadre: '',
    nombreRepresentante: '',
    cedulaRepresentante: '',
    telefono: '',
    email: '',
    fechaDocumento: new Date().toISOString().split('T')[0],
    firmaDigital: null,
  });

  const [errors, setErrors] = useState({});
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);

  // Mobile fullscreen signature modal
  const [isMobileSignatureOpen, setIsMobileSignatureOpen] = useState(false);
  const mobileCanvasRef = useRef(null);
  const [isMobileDrawing, setIsMobileDrawing] = useState(false);
  const [hasMobileSigned, setHasMobileSigned] = useState(false);

  // Floating hint
  const [showFloatingHint, setShowFloatingHint] = useState(false);
  const firmaSectionRef = useRef(null);
  const floatingHintTimerRef = useRef(null);
  const modalBodyRef = useRef(null);

  const isMobile = () => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  };

  // IntersectionObserver para hint flotante
  useEffect(() => {
    if (!isOpen || !firmaSectionRef.current) return;

    const scrollContainer = modalBodyRef.current;
    if (!scrollContainer) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (floatingHintTimerRef.current) clearTimeout(floatingHintTimerRef.current);
            setShowFloatingHint(true);
            floatingHintTimerRef.current = setTimeout(() => setShowFloatingHint(false), 3000);
          }
        });
      },
      { root: scrollContainer, threshold: 0.3 }
    );

    observer.observe(firmaSectionRef.current);

    return () => {
      observer.disconnect();
      if (floatingHintTimerRef.current) clearTimeout(floatingHintTimerRef.current);
    };
  }, [isOpen]);

  // Mobile canvas setup
  const setupMobileCanvas = useCallback(() => {
    const canvas = mobileCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const parent = canvas.parentElement;
    if (parent) {
      canvas.width = parent.clientWidth - 32;
      canvas.height = parent.clientHeight - 16;
    }
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  useEffect(() => {
    if (isMobileSignatureOpen) {
      const timer = setTimeout(setupMobileCanvas, 100);
      return () => clearTimeout(timer);
    }
  }, [isMobileSignatureOpen, setupMobileCanvas]);

  // Mobile drawing handlers
  const startMobileDrawing = (e) => {
    e.preventDefault();
    const canvas = mobileCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    setIsMobileDrawing(true);
    ctx.beginPath();
    const clientX = e.touches?.[0]?.clientX ?? e.clientX ?? 0;
    const clientY = e.touches?.[0]?.clientY ?? e.clientY ?? 0;
    ctx.moveTo(clientX - rect.left, clientY - rect.top);
  };

  const mobileDrawing = (e) => {
    if (!isMobileDrawing) return;
    e.preventDefault();
    const canvas = mobileCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000000';

    const clientX = e.touches?.[0]?.clientX ?? e.clientX ?? 0;
    const clientY = e.touches?.[0]?.clientY ?? e.clientY ?? 0;

    ctx.lineTo(clientX - rect.left, clientY - rect.top);
    ctx.stroke();
    setHasMobileSigned(true);
  };

  const stopMobileDrawing = () => setIsMobileDrawing(false);

  const limpiarFirmaMobile = () => {
    const canvas = mobileCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasMobileSigned(false);
  };

  const confirmarFirmaMobile = () => {
    if (hasMobileSigned && mobileCanvasRef.current) {
      const firmaDataURL = mobileCanvasRef.current.toDataURL('image/png');
      setFormData((prev) => ({ ...prev, firmaDigital: firmaDataURL }));
      setHasSigned(true);

      const mainCanvas = canvasRef.current;
      if (mainCanvas) {
        const ctx = mainCanvas.getContext('2d');
        if (ctx) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            ctx.clearRect(0, 0, mainCanvas.width, mainCanvas.height);
            ctx.drawImage(img, 0, 0, mainCanvas.width, mainCanvas.height);
          };
          img.src = firmaDataURL;
        }
      }
    }
    setIsMobileSignatureOpen(false);
  };

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.overflow = 'hidden';
      document.body.style.width = '100%';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.overflow = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.overflow = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  const validarNombreCompleto = (valor) => {
    const palabras = valor.trim().split(/\s+/).filter((p) => p.length > 0);
    return palabras.length >= 2;
  };

  const soloNumeros = (e) => {
    const valor = e.target.value.replace(/\D/g, '');
    const { name } = e.target;
    setFormData((prev) => ({ ...prev, [name]: valor }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const soloLetras = (e) => {
    const valor = e.target.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s']/g, '');
    const { name } = e.target;
    setFormData((prev) => ({ ...prev, [name]: valor }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    setIsDrawing(true);
    ctx.beginPath();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    ctx.moveTo(clientX - rect.left, clientY - rect.top);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000000';

    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;

    ctx.lineTo(clientX - rect.left, clientY - rect.top);
    ctx.stroke();
    setHasSigned(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    if (hasSigned && canvasRef.current) {
      const firmaDataURL = canvasRef.current.toDataURL('image/png');
      setFormData((prev) => ({ ...prev, firmaDigital: firmaDataURL }));
    }
  };

  const limpiarFirma = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
    setFormData((prev) => ({ ...prev, firmaDigital: null }));
  };

  const tieneDospalabras = (valor) => {
    return valor.trim().split(/\s+/).filter((p) => p.length > 1).length >= 2;
  };

  const validarFormulario = () => {
    const nuevosErrores = {};

    // Menor
    if (!formData.nombreMenor.trim()) {
      nuevosErrores.nombreMenor = 'Requerido';
    } else if (!tieneDospalabras(formData.nombreMenor)) {
      nuevosErrores.nombreMenor = 'Debe tener al menos nombre y apellido';
    }

    if (!formData.numeroDocumentoMenor.trim()) {
      nuevosErrores.numeroDocumentoMenor = 'Requerido';
    } else if (formData.numeroDocumentoMenor.length !== 10) {
      nuevosErrores.numeroDocumentoMenor = 'Debe tener exactamente 10 dígitos';
    }

    // Responsable
    if (formData.tipoResponsable === 'padre') {
      if (!formData.nombrePadre.trim()) {
        nuevosErrores.nombrePadre = 'Requerido';
      } else if (!tieneDospalabras(formData.nombrePadre)) {
        nuevosErrores.nombrePadre = 'Debe tener al menos nombre y apellido';
      }
      if (!formData.cedulaPadre.trim()) {
        nuevosErrores.cedulaPadre = 'Requerido';
      } else if (formData.cedulaPadre.length < 7 || formData.cedulaPadre.length > 10) {
        nuevosErrores.cedulaPadre = 'Cédula inválida (7–10 dígitos)';
      }
    } else if (formData.tipoResponsable === 'madre') {
      if (!formData.nombreMadre.trim()) {
        nuevosErrores.nombreMadre = 'Requerido';
      } else if (!tieneDospalabras(formData.nombreMadre)) {
        nuevosErrores.nombreMadre = 'Debe tener al menos nombre y apellido';
      }
      if (!formData.cedulaMadre.trim()) {
        nuevosErrores.cedulaMadre = 'Requerido';
      } else if (formData.cedulaMadre.length < 7 || formData.cedulaMadre.length > 10) {
        nuevosErrores.cedulaMadre = 'Cédula inválida (7–10 dígitos)';
      }
    } else if (formData.tipoResponsable === 'representante') {
      if (!formData.nombreRepresentante.trim()) {
        nuevosErrores.nombreRepresentante = 'Requerido';
      } else if (!tieneDospalabras(formData.nombreRepresentante)) {
        nuevosErrores.nombreRepresentante = 'Debe tener al menos nombre y apellido';
      }
      if (!formData.cedulaRepresentante.trim()) {
        nuevosErrores.cedulaRepresentante = 'Requerido';
      } else if (formData.cedulaRepresentante.length < 7 || formData.cedulaRepresentante.length > 10) {
        nuevosErrores.cedulaRepresentante = 'Cédula inválida (7–10 dígitos)';
      }
    }

    // Teléfono → OBLIGATORIO
    if (!formData.telefono.trim()) {
      nuevosErrores.telefono = 'El celular es obligatorio';
    } else if (formData.telefono.length !== 10) {
      nuevosErrores.telefono = 'Debe tener exactamente 10 dígitos';
    }

    // Firma
    if (!formData.firmaDigital) {
      nuevosErrores.firmaDigital = 'Por favor firme en el recuadro';
    }

    // Email → NO obligatorio

    setErrors(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const resetFormulario = () => {
    setFormData({
      nombreMenor: userName || '',
      tipoDocumentoMenor: 'TI',
      numeroDocumentoMenor: '',
      tipoResponsable: 'madre',
      nombrePadre: '',
      cedulaPadre: '',
      nombreMadre: '',
      cedulaMadre: '',
      nombreRepresentante: '',
      cedulaRepresentante: '',
      telefono: '',
      email: '',
      fechaDocumento: new Date().toISOString().split('T')[0],
      firmaDigital: null,
    });
    setErrors({});
    setHasSigned(false);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (!validarFormulario()) {
      alert('Por favor completa todos los campos obligatorios marcados con *');
      return;
    }

    generarContratoGymartPDF(formData);
    console.log('Formulario enviado:', formData);

    setTimeout(() => {
      resetFormulario();
      onClose(false);
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalDialog}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <h5 className={styles.modalTitle}>📋 Contrato de Afiliación - Club Gymart</h5>
            <button
              type="button"
              className={styles.btnClose}
              onClick={() => { resetFormulario(); onClose(false); }}
              aria-label="Cerrar"
            >
              ✕
            </button>
          </div>

          <div className={styles.modalBody} ref={modalBodyRef}>
            <form onSubmit={handleSubmit} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>
              {/* DATOS DEL MENOR */}
              <section className={styles.contratoSection}>
                <h6 className={styles.sectionTitle}>
                  <span className={styles.sectionIcon}>👶</span> Datos del Niño, Niña o Adolescente
                </h6>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Nombre Completo *</label>
                  <input
                    type="text"
                    className={styles.formControl}
                    name="nombreMenor"
                    value={formData.nombreMenor}
                    onChange={soloLetras}
                  />
                  {errors.nombreMenor && <div className={styles.textDanger}>{errors.nombreMenor}</div>}
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formColHalf}>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Tipo de Documento *</label>
                      <select
                        className={styles.formControl}
                        name="tipoDocumentoMenor"
                        value={formData.tipoDocumentoMenor}
                        onChange={handleChange}
                      >
                        <option value="TI">TI - Tarjeta de Identidad</option>
                        <option value="RCN">RCN - Registro Civil</option>
                      </select>
                    </div>
                  </div>

                  <div className={styles.formColHalf}>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Número de Documento * (10 dígitos)</label>
                      <input
                        type="text"
                        className={styles.formControl}
                        name="numeroDocumentoMenor"
                        value={formData.numeroDocumentoMenor}
                        onChange={soloNumeros}
                        maxLength={10}
                        inputMode="numeric"
                        placeholder="0000000000"
                      />
                      <small style={{ color: formData.numeroDocumentoMenor.length === 10 ? '#28a745' : '#6c757d' }}>
                        {formData.numeroDocumentoMenor.length}/10 dígitos
                      </small>
                      {errors.numeroDocumentoMenor && (
                        <div className={styles.textDanger}>{errors.numeroDocumentoMenor}</div>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              {/* RESPONSABLE */}
              <section className={styles.contratoSection}>
                <h6 className={styles.sectionTitle}>
                  <span className={styles.sectionIcon}>👨‍👩‍👦</span> Responsable del Menor
                </h6>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>¿Quién firma como responsable? *</label>
                  <div className={styles.radioGroup}>
                    <label className={styles.radioItem}>
                      <input
                        type="radio"
                        name="tipoResponsable"
                        value="madre"
                        checked={formData.tipoResponsable === 'madre'}
                        onChange={handleChange}
                      />
                      <span className={styles.radioLabel}>Madre</span>
                    </label>
                    <label className={styles.radioItem}>
                      <input
                        type="radio"
                        name="tipoResponsable"
                        value="padre"
                        checked={formData.tipoResponsable === 'padre'}
                        onChange={handleChange}
                      />
                      <span className={styles.radioLabel}>Padre</span>
                    </label>
                    <label className={styles.radioItem}>
                      <input
                        type="radio"
                        name="tipoResponsable"
                        value="representante"
                        checked={formData.tipoResponsable === 'representante'}
                        onChange={handleChange}
                      />
                      <span className={styles.radioLabel}>Representante Legal</span>
                    </label>
                  </div>
                </div>

                <div className={styles.responsableFields}>
                  {formData.tipoResponsable === 'madre' && (
                    <>
                      <h6 className={styles.subsectionTitle}>Datos de la Madre</h6>
                      <div className={styles.formRow}>
                        <div className={styles.formColHalf}>
                          <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Nombre Completo *</label>
                            <input
                              type="text"
                              className={styles.formControl}
                              name="nombreMadre"
                              value={formData.nombreMadre}
                              onChange={soloLetras}
                            />
                            {errors.nombreMadre && <div className={styles.textDanger}>{errors.nombreMadre}</div>}
                          </div>
                        </div>
                        <div className={styles.formColHalf}>
                          <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Cédula de Ciudadanía *</label>
                            <input
                              type="text"
                              className={styles.formControl}
                              name="cedulaMadre"
                              value={formData.cedulaMadre}
                              onChange={soloNumeros}
                              maxLength={10}
                              inputMode="numeric"
                              placeholder="0000000000"
                            />
                            {errors.cedulaMadre && <div className={styles.textDanger}>{errors.cedulaMadre}</div>}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {formData.tipoResponsable === 'padre' && (
                    <>
                      <h6 className={styles.subsectionTitle}>Datos del Padre</h6>
                      <div className={styles.formRow}>
                        <div className={styles.formColHalf}>
                          <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Nombre Completo *</label>
                            <input
                              type="text"
                              className={styles.formControl}
                              name="nombrePadre"
                              value={formData.nombrePadre}
                              onChange={soloLetras}
                            />
                            {errors.nombrePadre && <div className={styles.textDanger}>{errors.nombrePadre}</div>}
                          </div>
                        </div>
                        <div className={styles.formColHalf}>
                          <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Cédula de Ciudadanía *</label>
                            <input
                              type="text"
                              className={styles.formControl}
                              name="cedulaPadre"
                              value={formData.cedulaPadre}
                              onChange={soloNumeros}
                              maxLength={10}
                              inputMode="numeric"
                              placeholder="0000000000"
                            />
                            {errors.cedulaPadre && <div className={styles.textDanger}>{errors.cedulaPadre}</div>}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {formData.tipoResponsable === 'representante' && (
                    <>
                      <h6 className={styles.subsectionTitle}>Datos del Representante Legal</h6>
                      <div className={styles.formRow}>
                        <div className={styles.formColHalf}>
                          <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Nombre Completo *</label>
                            <input
                              type="text"
                              className={styles.formControl}
                              name="nombreRepresentante"
                              value={formData.nombreRepresentante}
                              onChange={soloLetras}
                            />
                            {errors.nombreRepresentante && (
                              <div className={styles.textDanger}>{errors.nombreRepresentante}</div>
                            )}
                          </div>
                        </div>
                        <div className={styles.formColHalf}>
                          <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Cédula de Ciudadanía *</label>
                            <input
                              type="text"
                              className={styles.formControl}
                              name="cedulaRepresentante"
                              value={formData.cedulaRepresentante}
                              onChange={soloNumeros}
                              maxLength={10}
                              inputMode="numeric"
                              placeholder="0000000000"
                            />
                            {errors.cedulaRepresentante && (
                              <div className={styles.textDanger}>{errors.cedulaRepresentante}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </section>

              {/* FIRMA */}
              <section className={styles.contratoSection} ref={firmaSectionRef}>
                <h6 className={styles.sectionTitle}>
                  <span className={styles.sectionIcon}>✍️</span> Firma Digital del Acudiente *
                </h6>

                {showFloatingHint && (
                  <div className={styles.floatingHint}>
                    <span className={styles.floatingHintIcon}>👆</span>
                    {isMobile()
                      ? 'Toque el recuadro para abrir el panel de firma'
                      : 'Firme con el mouse o dedo en el recuadro'}
                  </div>
                )}

                <div className={styles.firmaContainer}>
                  <p className={styles.firmaHint}>
                    {isMobile() ? 'Toque para abrir panel de firma:' : 'Firme aquí:'}
                  </p>

                  <div className={styles.canvasWrapper}>
                    <canvas
                      ref={canvasRef}
                      className={`${styles.firmaCanvas} ${errors.firmaDigital ? styles.borderDanger : ''}`}
                      width={600}
                      height={200}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={(e) => isMobile() ? setIsMobileSignatureOpen(true) : startDrawing(e)}
                      onTouchMove={(e) => !isMobile() && draw(e)}
                      onTouchEnd={() => !isMobile() && stopDrawing()}
                    />
                    {isMobile() && !hasSigned && (
                      <div
                        className={styles.mobileCanvasOverlay}
                        onClick={() => setIsMobileSignatureOpen(true)}
                        role="button"
                        tabIndex={0}
                      >
                        <span className={styles.mobileCanvasOverlayIcon}>✍️</span>
                        <span>Toque para firmar</span>
                      </div>
                    )}
                    {isMobile() && hasSigned && (
                      <div
                        className={styles.mobileEditFirmaBadge}
                        onClick={() => setIsMobileSignatureOpen(true)}
                        role="button"
                        tabIndex={0}
                      >
                        Editar firma
                      </div>
                    )}
                  </div>

                  {errors.firmaDigital && (
                    <div className={styles.textDangerCenter}>{errors.firmaDigital}</div>
                  )}

                  <div className={styles.firmaBtnContainer}>
                    <button type="button" className={`${styles.btn} ${styles.btnLimpiar}`} onClick={limpiarFirma}>
                      Limpiar Firma
                    </button>
                  </div>
                </div>
              </section>

              {/* CONTACTO */}
              <section className={styles.contratoSection}>
                <h6 className={styles.sectionTitle}>
                  <span className={styles.sectionIcon}>📧</span> Información de Contacto
                </h6>

                <div className={styles.formRow}>
                  <div className={styles.formColHalf}>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Celular * (10 dígitos)</label>
                      <input
                        type="text"
                        className={styles.formControl}
                        name="telefono"
                        value={formData.telefono}
                        onChange={soloNumeros}
                        maxLength={10}
                        inputMode="numeric"
                        placeholder="3001234567"
                      />
                      <small style={{ color: formData.telefono.length === 10 ? '#28a745' : '#6c757d' }}>
                        {formData.telefono.length}/10 dígitos
                      </small>
                      {errors.telefono && <div className={styles.textDanger}>{errors.telefono}</div>}
                    </div>
                  </div>

                  <div className={styles.formColHalf}>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Email (opcional)</label>
                      <input
                        type="email"
                        className={styles.formControl}
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </section>
            </form>
          </div>

          <div className={styles.modalFooter}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnCancelar}`}
              onClick={() => { resetFormulario(); onClose(false); }}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnGenerar}`}
              onClick={handleSubmit}
            >
              📄 Generar Contrato PDF
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Signature Modal */}
      {isMobileSignatureOpen && (
        <div className={styles.mobileSignatureModal}>
          <div className={styles.mobileSignatureHeader}>
            <h6 className={styles.mobileSignatureTitle}>Firme aquí</h6>
            <button
              type="button"
              className={styles.mobileSignatureCloseBtn}
              onClick={() => setIsMobileSignatureOpen(false)}
              aria-label="Cerrar"
            >
              x
            </button>
          </div>

          <div className={styles.mobileSignatureInstruction}>
            Use su dedo para firmar en el espacio blanco
          </div>

          <div className={styles.mobileSignatureCanvasArea}>
            <canvas
              ref={mobileCanvasRef}
              className={styles.mobileSignatureCanvas}
              onTouchStart={startMobileDrawing}
              onTouchMove={mobileDrawing}
              onTouchEnd={stopMobileDrawing}
              onMouseDown={startMobileDrawing}
              onMouseMove={mobileDrawing}
              onMouseUp={stopMobileDrawing}
              onMouseLeave={stopMobileDrawing}
            />
          </div>

          <div className={styles.mobileSignatureFooter}>
            <button type="button" className={styles.mobileSignatureBtnLimpiar} onClick={limpiarFirmaMobile}>
              Limpiar
            </button>
            <button
              type="button"
              className={hasMobileSigned ? styles.mobileSignatureBtnConfirmarActive : styles.mobileSignatureBtnConfirmar}
              onClick={confirmarFirmaMobile}
              disabled={!hasMobileSigned}
            >
              Confirmar Firma
            </button>
          </div>
        </div>
      )}
    </div>
  );
};